import React from 'react';

interface ContentHardenerProps {
    title: string;
    category?: string;
}

const ContentHardener = ({ title, category }: ContentHardenerProps) => {
    const normalizedCategory = (() => {
        const cat = (category || '').toLowerCase();
        if (cat.includes('financial') || cat.includes('fiscal') || cat.includes('tax')) return 'Financial';
        if (cat.includes('marketing') || cat.includes('ai') || cat.includes('business')) return 'Marketing';
        if (cat.includes('developer') || cat.includes('json') || cat.includes('code')) return 'Developer';
        if (cat.includes('security') || cat.includes('password') || cat.includes('crypto')) return 'Security';
        if (cat.includes('text') || cat.includes('content') || cat.includes('utility')) return 'Content';
        return 'General';
    })();

    const getContent = () => {
        switch (normalizedCategory) {
            case 'Financial':
                return {
                    heading: `Local Calculation Logic: ${title}`,
                    paragraphs: [
                        `Processing financial models, loan schedules, or tax distributions on public web platforms risks exposing sensitive business metrics. We engineered ${title} to operate entirely in local memory, resolving calculations within your browser context without any network dependencies.`,
                        `To guarantee mathematical accuracy, the calculation engine relies on double-precision arithmetic. This prevents standard rounding discrepancies and float anomalies during large-scale calculations, returning exact totals instantly.`,
                        `All user-entered variables—including principal amounts, interest rates, and tax calculations—stay isolated in your current browser tab. Closing or refreshing the page instantly releases the allocated memory, ensuring no local cache or analytics footprints remain.`
                    ],
                    badge1: '✓ Local Math Engine',
                    desc1: 'Resolves all formulas instantly inside your browser tab without external API queries.',
                    badge2: '✓ Precise Arithmetic',
                    desc2: 'Prevents standard JavaScript float issues using double-precision calculations.'
                };
            case 'Security':
                return {
                    heading: `Isolated Cryptographic Verification: ${title}`,
                    paragraphs: [
                        `Generating keys, testing inputs, or decoding headers requires secure environments. Transmitting credentials to remote servers violates basic compliance principles. We built ${title} to execute purely on the client side, keeping sensitive hashes and strings local.`,
                        `The application leverages native web APIs to extract high-strength random entropy directly from the underlying operating system. This secures key generation against predictable patterns and brute-force cracking models.`,
                        `No inputs, credentials, or outputs are cached on disk or sent to secondary logs. The ephemeral session runtime is terminated and wiped clean from temporary RAM as soon as you navigate away.`
                    ],
                    badge1: '✓ Native Web Crypto',
                    desc1: 'Taps into secure browser-level entropy sources for unpredictable key generation.',
                    badge2: '✓ Volatile Storage',
                    desc2: 'Confines keys to ephemeral memory, leaving no permanent system footprints.'
                };
            case 'Developer':
                return {
                    heading: `Local Parser & Syntax Validation: ${title}`,
                    paragraphs: [
                        `Debugging payloads, translating syntaxes, or pretty-printing configurations shouldn't introduce compliance liabilities. ${title} processes code blocks and schemas entirely inside your active workspace, ensuring zero network latency.`,
                        `By offloading heavy parsing actions to isolated Web Worker threads, we keep the main UI responsive. Large datasets are processed without freezing the viewport or triggering browser timeouts.`,
                        `The formatting parser respects standard structural specifications (such as ECMA-404 for JSON). This guarantees that output styles are correctly structured for clean code reviews and version control diffs.`
                    ],
                    badge1: '✓ Worker Offloading',
                    desc1: 'Delegates complex parser operations to background threads to prevent UI lag.',
                    badge2: '✓ Standard Compliant',
                    desc2: 'Formats code according to strict syntax specifications without silent truncation.'
                };
            case 'Content':
                return {
                    heading: `High-Fidelity Text Formatting: ${title}`,
                    paragraphs: [
                        `Manipulating lists, cleaning lines, or checking diffs on server-side utilities slows down daily work. ${title} provides local text processing to handle multi-megabyte string blocks without network round-trips.`,
                        `The engine preserves original text formatting, handling Unicode ranges and internationalized character scripts correctly. This prevents character corruption when working with multilingual lists.`,
                        `All text transformations are performed locally. Once you copy the formatted output and close the workspace, all raw string data is completely cleared from volatile memory.`
                    ],
                    badge1: '✓ UTF-8 Integrity',
                    desc1: 'Supports multi-byte international characters and ligatures without corruption.',
                    badge2: '✓ In-Memory Sorting',
                    desc2: 'Processes large text lists in local workspace memory with rapid response times.'
                };
            case 'Marketing':
                return {
                    heading: `Secure Strategy Planning: ${title}`,
                    paragraphs: [
                        `Structuring campaigns, generating ideas, or planning strategies requires protecting proprietary concepts. ${title} operates completely locally, safeguarding your business plans and campaign briefs from public training sets.`,
                        `By utilizing client-side execution, we ensure that your product specifications and audience outlines are processed privately. You get instant drafts and structured suggestions locally.`,
                        `Your concepts and variables remain in your browser session. The data is never logged, stored, or processed externally, allowing teams to prototype campaigns securely.`
                    ],
                    badge1: '✓ Secure Prototyping',
                    desc1: 'Ensures business strategies and target plans remain confidential.',
                    badge2: '✓ No External Logging',
                    desc2: 'Protects campaign descriptions and outlines from tracking.'
                };
            default:
                return {
                    heading: `Client-Side Execution Context: ${title}`,
                    paragraphs: [
                        `Operating in modern production environments requires tools that minimize data exposure. We built ${title} to run entirely in the browser context, eliminating server-side transfers and cloud latency.`,
                        `Confining computational steps to local logic ensures your inputs are converted and returned instantly, matching the speed of native desktop applications.`,
                        `Closing your workspace tab purges the session memory. No cookies, trackers, or persistent local storage databases are used to maintain logs of your activity.`
                    ],
                    badge1: '✓ Zero Cloud Trailing',
                    desc1: 'No server-side uploads, eliminating data collection by design.',
                    badge2: '✓ In-Browser Execution',
                    desc2: 'Executes logic directly within the local browser environment for maximum speed.'
                };
        }
    };

    const content = getContent();

    return (
        <section style={{ marginBottom: '8rem', padding: '4rem', background: '#fff', borderRadius: '48px', border: '1px solid #f1f5f9', boxShadow: '0 15px 40px rgba(0,0,0,0.02)' }}>
            <h2 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 900, marginBottom: '2.5rem', color: '#0f172a', letterSpacing: '-0.04em' }}>
                {content.heading}
            </h2>
            <div style={{ lineHeight: '2.2', color: '#475569', fontSize: '1.15rem' }}>
                {content.paragraphs.map((p, idx) => (
                    <p key={idx} style={{ marginBottom: idx === content.paragraphs.length - 1 ? '0' : '2rem' }}>
                        {p}
                    </p>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '3rem', marginTop: '4rem', borderTop: '1px solid #f1f5f9', paddingTop: '3rem' }}>
                <div>
                    <div style={{ fontWeight: 900, color: 'var(--primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>{content.badge1}</div>
                    <div style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: '1.6' }}>{content.desc1}</div>
                </div>
                <div>
                    <div style={{ fontWeight: 900, color: 'var(--primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>✓ Zero Server Logs</div>
                    <div style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: '1.6' }}>No remote databases or external APIs are contacted. Your raw inputs remain strictly private.</div>
                </div>
                <div>
                    <div style={{ fontWeight: 900, color: 'var(--primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>{content.badge2}</div>
                    <div style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: '1.6' }}>{content.desc2}</div>
                </div>
                <div>
                    <div style={{ fontWeight: 900, color: 'var(--primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>✓ Ephemeral RAM Only</div>
                    <div style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: '1.6' }}>All memory allocations are released instantly upon closing or reloading the active tab.</div>
                </div>
            </div>
        </section>
    );
};

export default ContentHardener;
